// hooks/useAssistantRuntime.ts
import {
  ChatModelAdapter,
  TextMessagePart,
  ThreadAssistantMessagePart,
  ThreadUserMessagePart,
} from "@assistant-ui/react";
import { StudyDocument, StudyNotUploadedDocument } from "../types";
import {
  postStudyNotebooksByIdHighlights,
  postStudyNotebooksByNotebookIdChatByChatIdMessage,
} from "../client";
import { HIGHLIGHT_CONFIG } from "../components/layout/highlights/highlights";
import { useAuth } from "@/lib/auth";
import { NotebookContextValue, useNotebookContext } from "./useNotebook";
import { CanvasCourse } from "@/lib/types";

export const TOOL_PREFIX = "[__bettercampus__tool__]";
export const TRIAL_GATE_TOOL = "[__bettercampus_gate__]";

// =============================================================
// Tool Execution Function
// =============================================================
async function executeTool(
  toolName: string,
  args: any,
  notebookId: string,
  selectedDocumentIds: number[]
) {
  const highlightConfig = HIGHLIGHT_CONFIG.find((x) => x.label === toolName);
  const highlightType = highlightConfig?.id;
  if (!highlightType)
    throw Error(`Failed to create highlight. Invalid type ${toolName}`);
  const { data, error, response } = await postStudyNotebooksByIdHighlights({
    path: {
      id: notebookId,
    },
    body: {
      name: args["topic"],
      config_prompt: highlightConfig.configPrompt.makePrompt(args),
      notebook_document_ids: selectedDocumentIds,
      type: highlightType,
    },
  });
  if (response.status === 402) throw { error, status: 402 };
  if (error)
    throw Error(`Failed to create highlight. Error: ${JSON.stringify(error)}`);

  return data;
}

async function getOrCreateNotebookWithFile(
  courseFile: StudyNotUploadedDocument,
  course: CanvasCourse,
  createNotebook: NotebookContextValue["createNotebook"],
  uploadCourseSource: (
    document: StudyNotUploadedDocument,
    notebookId: string
  ) => Promise<StudyDocument>
) {
  const notebook = await createNotebook({
    name: courseFile.name,
    course: course,
    fileId: courseFile.metadata.canvas_file_id.toString(),
  });

  if ("notebook_documents" in notebook && notebook.notebook_documents.length)
    return {
      notebook,
      document: notebook.notebook_documents[0],
    };

  // upload file if first creation
  const document = await uploadCourseSource(
    {
      ...courseFile,
      notebook_id: notebook.id,
    },
    notebook.id.toString()
  );

  return { notebook, document };
}

// =============================================================
// Model Adapter with Tool Support
// =============================================================
export const makeModelAdapter = (
  notebookId: string,
  selectedDocuments: StudyDocument[],
  fileDetails?: {
    courseFile: StudyNotUploadedDocument;
    course: CanvasCourse;
    createNotebook: NotebookContextValue["createNotebook"];
    uploadCourseSource: (
      document: StudyNotUploadedDocument,
      notebookId: string
    ) => Promise<StudyDocument>;
  }
) => {
  const shouldFirstCreate = notebookId === "-1"; // for files chat
  let selectedDocumentIds = selectedDocuments.map((doc) => doc.id);

  const adapter: ChatModelAdapter = {
    async *run({ messages, abortSignal }) {
      console.log("adapter", messages);
      // Check if the last message contains a tool call query
      const lastMessage = messages[messages.length - 1];

      if (messages.length === 1 && shouldFirstCreate && fileDetails) {
        const { notebook, document } = await getOrCreateNotebookWithFile(
          fileDetails.courseFile,
          fileDetails.course,
          fileDetails.createNotebook,
          fileDetails.uploadCourseSource
        );
        notebookId = notebook.id.toString();
        selectedDocumentIds = [document.id];
      }

      const textPart = (
        lastMessage?.content as (
          | ThreadUserMessagePart
          | ThreadAssistantMessagePart
        )[]
      )?.find(
        (c) =>
          c.type === "text" &&
          typeof c.text === "string" &&
          c.text.startsWith(`${TOOL_PREFIX}:`)
      ) as TextMessagePart | undefined;

      // =============================================================
      // Handle Tool Call (from user text prefix)
      // =============================================================
      if (textPart) {
        const raw = textPart.text.slice(TOOL_PREFIX.length + 1).trim();

        // split: "<toolName> <json args>"
        const [toolName, ...rest] = raw.split(" ");
        const [configJSON, ...query] = rest;
        const argsText = query.join(" ").trim();

        // TODO (potentially): support other tool calls
        const args = { topic: argsText, ...JSON.parse(configJSON) };
        const toolCallId = crypto.randomUUID();

        try {
          yield {
            content: [
              {
                type: "tool-call",
                toolName,
                toolCallId,
                args,
                argsText,
              },
            ],
          };
          // Execute the tool
          const result = await executeTool(
            toolName,
            args,
            notebookId,
            selectedDocumentIds
          );

          // Yield the tool result
          yield {
            content: [
              {
                type: "tool-call",
                toolName,
                toolCallId,
                result,
                args: {},
                argsText: "",
              },
            ],
          };
        } catch (error: any) {
          console.error(error);
          if ("status" in error && error.status === 402) {
            yield {
              content: [
                {
                  type: "tool-call",
                  toolName: TRIAL_GATE_TOOL,
                  toolCallId,
                  result: {
                    error: error.error.error,
                    period: error.error.period,
                  },
                  isError: true,
                  args: {
                    type: "highlights",
                    period: error.error.period,
                  },
                  argsText: "",
                },
              ],
            };
            return;
          }
          // Yield error result
          yield {
            content: [
              {
                type: "tool-call",
                toolName,
                toolCallId,
                result: { error: error.message },
                isError: true,
                args: {},
                argsText: "",
              },
            ],
          };
        }
        return;
      }

      // =============================================================
      // Handle Regular Chat
      //  - filter all user TOOL_PREFIX messages and assistant tool-call messages
      // =============================================================
      const textMessages = messages.filter(
        (m) =>
          m.content.filter(
            (c) =>
              c.type === "text" &&
              c.text.slice(0, TOOL_PREFIX.length) !== TOOL_PREFIX
          ).length
      );

      if (textMessages.length === 0) return;

      console.log("text messages", textMessages);

      const recentMessageContent = (
        textMessages[textMessages.length - 1].content.find(
          (c) => c.type === "text"
        ) as TextMessagePart
      ).text;
      const { stream } =
        await postStudyNotebooksByNotebookIdChatByChatIdMessage({
          // throw on error
          path: {
            notebook_id: notebookId,
            chat_id: "1",
          },
          body: {
            query: {
              sender: "user",
              content: recentMessageContent,
            },
            history: textMessages
              .slice(0, textMessages.length - 1)
              .map((m) => ({
                sender: "ai",
                content:
                  (m.content.find((c) => c.type === "text") as TextMessagePart)
                    ?.text || "",
              })),
            notebook_document_ids: selectedDocumentIds,
          },
        });

      let text = "";
      for await (const data of stream) {
        if (
          !text &&
          (data as unknown as string).slice(0, TRIAL_GATE_TOOL.length) ===
            TRIAL_GATE_TOOL
        ) {
          yield {
            content: [
              {
                type: "tool-call",
                toolName: TRIAL_GATE_TOOL,
                args: {
                  type: "chat",
                  period: (data as unknown as string).slice(
                    TRIAL_GATE_TOOL.length
                  ),
                },
              },
            ],
          };
          continue;
        }
        text += (data as unknown as string).slice(1);

        yield {
          content: [
            {
              type: "text",
              text,
            },
          ],
        };
      }
    },
  };

  return adapter;
};

// =============================================================
// Helper to Manually Invoke Tools (for suggestion buttons)
// =============================================================
export function createToolCall(
  toolName: "generate_flashcards" | "generate_quiz" | "generate_summary",
  args: any
) {
  return {
    role: "user" as const,
    content: [
      {
        type: "tool-call" as const,
        toolCallId: `call-${Date.now()}`,
        toolName,
        args,
      },
    ],
  };
}

// =============================================================
// Usage Example
// =============================================================
/*
// When suggestion button is clicked:
import { useThreadRuntime } from "@assistant-ui/react";

function SuggestionButton() {
  const runtime = useThreadRuntime();
  
  const handleClick = () => {
    const toolCall = createToolCall("generate_flashcards", {
      topic: "photosynthesis",
      count: 10,
    });
    
    runtime.append(toolCall);
  };
  
  return <button onClick={handleClick}>Generate Flashcards</button>;
}
*/
